import { useCallback, useRef, useState } from 'react';
import { Upload, Link as LinkIcon, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { toast } from '@/lib/toast';
import type { Block, ImageBlockContent } from '@/types';

interface ImageBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
}

function ImageBlock({ block, isEditable = false, onUpdate }: ImageBlockProps) {
  const content = block.content as ImageBlockContent;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) {
        toast.error('Selecteer een afbeelding (JPG, PNG, GIF, etc.)');
        return;
      }

      setUploading(true);
      try {
        const result = await api.uploadImage(file);
        onUpdate?.({
          content: {
            ...content,
            url: result.url,
            alt: file.name.replace(/\.[^/.]+$/, ''),
          },
        });
      } catch (error) {
        toast.error('Upload mislukt. Probeer het opnieuw.', { error });
      } finally {
        setUploading(false);
      }
    },
    [content, onUpdate]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onUpdate?.({
        content: {
          ...content,
          url: urlInput.trim(),
        },
      });
      setShowUrlInput(false);
      setUrlInput('');
    }
  };

  if (!content.url && isEditable) {
    return (
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          'flex h-full w-full cursor-pointer flex-col items-center justify-center border-2 border-dashed p-8 text-center transition-all',
          dragOver ? 'border-primary bg-primary/5' : 'border-gray-300 bg-gray-50'
        )}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
        />

        {uploading ? (
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-muted-foreground" />
        ) : (
          <>
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-muted-foreground mb-4">
              Sleep een afbeelding hierheen of klik om te uploaden
            </p>
            <div className="flex gap-2 justify-center">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <Upload className="h-4 w-4 mr-1" />
                Upload
              </Button>
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUrlInput(true);
                }}
              >
                <LinkIcon className="h-4 w-4 mr-1" />
                URL
              </Button>
            </div>

            {showUrlInput && (
              <div
                className="mt-4 flex gap-2 justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <Input
                  placeholder="https://example.com/image.jpg"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                  className="w-[300px]"
                />
                <Button onClick={handleUrlSubmit}>
                  Toevoegen
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  if (!content.url) {
    return <div className="h-full w-full bg-white" />;
  }

  return (
    <div className="relative h-full w-full overflow-hidden bg-black" data-full-bleed-surface>
      {isEditable && (
        <div className="absolute right-3 top-3 z-20 flex items-center gap-2 rounded-lg bg-white/95 p-2 shadow-lg backdrop-blur">
          <Input
            placeholder="Alt tekst"
            value={content.alt || ''}
            onChange={(e) => {
              onUpdate?.({
                content: { ...content, alt: e.target.value },
              });
            }}
            className="h-8 w-[180px] bg-white text-xs"
          />

          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => fileInputRef.current?.click()}
            title="Vervangen"
          >
            <Upload className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon-sm"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              onUpdate?.({
                content: { ...content, url: '', alt: '' },
              });
            }}
            title="Verwijderen"
          >
            <Trash2 className="h-4 w-4" />
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />
        </div>
      )}

      <img
        src={content.url}
        alt={content.alt || ''}
        data-full-bleed-artwork
        className="absolute inset-0 h-full w-full object-cover"
      />
    </div>
  );
}

export default ImageBlock;
